import {createContext, useLayoutEffect, useState} from 'react'
import {NavigationType, useLocation, useNavigationType} from '@github-ui/react-router'
import {SOFT_NAV_DURATION_MARK} from '@github-ui/soft-nav/stats'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {inSoftNav} from '@github-ui/soft-nav/utils'
import {sendCustomMetric} from '@github-ui/stats'
import type {FileMetadataCacheResult} from '../page-data/loaders/use-file-metadata'
import {createChangesLoadTiming, type ChangesLoadMode, type ChangesLoadTiming} from '../utils/changes-load-timing'

export const ChangesLoadTimingContext = createContext<ChangesLoadTiming | undefined>(undefined)

export function useChangesLoadTiming(
  expectedMode: ChangesLoadMode | undefined,
  cacheSplit: boolean,
  initialCacheResult?: FileMetadataCacheResult,
) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const createTiming = () =>
    expectedMode
      ? createChangesLoadTiming({
          expectedMode,
          cacheSplit,
          initialCacheResult,
          now: () => performance.now(),
          report: sendCustomMetric,
          reportDiffEntriesRequestCount: sendCustomMetric,
        })
      : undefined
  const [current, setCurrent] = useState(() => ({location, expectedMode, timing: createTiming()}))

  if (current.location.key !== location.key || current.expectedMode !== expectedMode) {
    const hashOnly =
      current.location.pathname === location.pathname &&
      current.location.search === location.search &&
      current.location.hash !== location.hash &&
      current.expectedMode === expectedMode
    setCurrent({location, expectedMode, timing: hashOnly ? current.timing : createTiming()})
  }

  const timing = current.timing
  useLayoutEffect(() => {
    if (!timing) return

    const mark = performance.getEntriesByName(SOFT_NAV_DURATION_MARK, 'mark').at(-1)
    const softNavigationActive = Boolean(inSoftNav())
    const isInitialHardNavigation =
      navigationType === NavigationType.Pop && location.key === 'default' && mark === undefined && !softNavigationActive
    const isInitialSoftNavigation =
      navigationType === NavigationType.Pop && location.key === 'default' && mark !== undefined && softNavigationActive
    if (navigationType === NavigationType.Pop && !isInitialHardNavigation && !isInitialSoftNavigation) {
      timing.cancel()
      return
    }

    timing.start({start: mark?.startTime ?? 0, type: mark ? 'soft' : 'hard'})
    document.addEventListener(SOFT_NAV_STATE.START, timing.cancel)
    document.addEventListener(SOFT_NAV_STATE.ERROR, timing.cancel)
    window.addEventListener('pagehide', timing.cancel)
    return () => {
      timing.pause()
      document.removeEventListener(SOFT_NAV_STATE.START, timing.cancel)
      document.removeEventListener(SOFT_NAV_STATE.ERROR, timing.cancel)
      window.removeEventListener('pagehide', timing.cancel)
    }
  }, [location.key, navigationType, timing])

  return timing
}
